import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Modal, Form, Button, Spinner, Alert } from 'react-bootstrap';
import moment from 'moment';
import { selectors as sessionSelectors } from '../../reducers/session';
import { getTareasTimesheet, postSaveTimesheet } from '../../api';
import { onError } from '../../libs/errorLib';

/**
 * Componente reutilizable para registrar horas de un recurso en una actividad
 * Gantt de un proyecto, desde cualquier vista donde aparezca el nombre del
 * recurso (Gantt, Sprint/Scrum, etc.). Punto 19 del plan.
 *
 * Props:
 * - show: boolean de apertura del modal.
 * - onClose(): cierra el modal.
 * - onRegistrado(): se ejecuta tras guardar una entrada con éxito.
 * - proyectoId (número, requerido): proyecto al que se imputan las horas.
 * - proyectoNombre (opcional): nombre para mostrar del proyecto.
 * - taskId / taskNombre (opcional): actividad Gantt preseleccionada (vista Gantt).
 * - usuarioId / nombreUsuario (opcional): recurso fijo al que se registran horas
 *   (por ej. el asignado en una historia). Si no se indica, se usa la selección
 *   o el usuario actual.
 * - usuariosOptions [{ id, nombre }] (opcional): lista de recursos a elegir
 *   (por ej. los asignados a una tarea del Gantt).
 */
function RegistrarTiempoModal({
    show,
    onClose,
    onRegistrado,
    proyectoId,
    proyectoNombre,
    taskId,
    taskNombre,
    usuarioId,
    nombreUsuario,
    usuariosOptions,
    usuarioActual,
}) {
    const [tareas, setTareas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [guardando, setGuardando] = useState(false);
    const [actividadId, setActividadId] = useState('');
    const [paraId, setParaId] = useState('');
    const [fecha, setFecha] = useState(moment().format('YYYY-MM-DD'));
    const [horas, setHoras] = useState('');
    const [error, setError] = useState(null);
    const [okMsg, setOkMsg] = useState(null);

    // Cargar las actividades Gantt del proyecto al abrir
    useEffect(() => {
        if (!show || !proyectoId) return;
        setCargando(true);
        setError(null);
        setOkMsg(null);
        setActividadId(taskId || '');
        getTareasTimesheet({ proyectoId })
            .then((res) => {
                const data = res.data;
                const lista = data && data.success && data.data && Array.isArray(data.data.tasks) ? data.data.tasks : [];
                setTareas(lista);
            })
            .catch((e) => {
                onError(e);
                setError('No se pudieron cargar las actividades del proyecto.');
            })
            .finally(() => setCargando(false));
    }, [show, proyectoId, taskId]);

    // Destinatario por defecto según contexto
    const defaultParaId = useMemo(() => {
        if (usuarioId) return String(usuarioId);
        if (Array.isArray(usuariosOptions) && usuariosOptions.length > 0) return String(usuariosOptions[0].id);
        return usuarioActual && usuarioActual.id ? String(usuarioActual.id) : '';
    }, [usuarioId, usuariosOptions, usuarioActual]);

    useEffect(() => {
        if (show) {
            setParaId(defaultParaId);
            setHoras('');
        }
    }, [show, defaultParaId]);

    const targetLabel = useMemo(() => {
        if (nombreUsuario) return nombreUsuario;
        if (Array.isArray(usuariosOptions) && usuariosOptions.length > 0) {
            const picked = usuariosOptions.find((o) => String(o.id) === String(paraId));
            return picked ? picked.nombre : 'Seleccione el recurso';
        }
        if (usuarioActual) return usuarioActual.username || 'Mí mismo';
        return '—';
    }, [nombreUsuario, usuariosOptions, paraId, usuarioActual]);

    const handleGuardar = async () => {
        setError(null);
        setOkMsg(null);

        if (!actividadId) {
            setError('Seleccione la actividad (Gantt) a la que se imputan las horas.');
            return;
        }
        const h = parseFloat(horas);
        if (!horas || isNaN(h) || h <= 0) {
            setError('Ingrese una cantidad de horas mayor a 0.');
            return;
        }
        if (h > 24) {
            setError('Las horas por día no pueden superar 24.');
            return;
        }
        if (!fecha) {
            setError('Seleccione la fecha.');
            return;
        }

        const targetId = paraId ? parseInt(paraId, 10) : null;
        const payload = {
            entries: [{
                task_id: actividadId,
                project_id: parseInt(proyectoId, 10),
                fecha: moment(fecha).format('YYYY-MM-DD'),
                horas: h,
            }],
        };
        if (targetId && String(targetId) !== String(usuarioActual && usuarioActual.id)) {
            payload.registroParaUsuarioId = targetId;
        }

        setGuardando(true);
        try {
            const res = await postSaveTimesheet(payload);
            if (res.data && res.data.success) {
                setOkMsg('Horas registradas correctamente.');
                if (onRegistrado) onRegistrado();
                setTimeout(() => {
                    setHoras('');
                    if (onClose) onClose();
                }, 900);
            } else {
                setError((res.data && res.data.message) || 'No se pudo registrar el tiempo.');
            }
        } catch (e) {
            onError(e);
            setError('No se pudo registrar el tiempo. Verifique sus permisos.');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-clock me-2" />
                    Registrar tiempo
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small mb-3">
                    Imputar horas de <strong>{targetLabel}</strong> en el proyecto{' '}
                    <strong>{proyectoNombre || `#${proyectoId}`}</strong>.
                </p>

                {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
                {okMsg && <Alert variant="success" className="py-2 small">{okMsg}</Alert>}

                <Form.Group className="mb-3">
                    <Form.Label>Registrar para</Form.Label>
                    {Array.isArray(usuariosOptions) && usuariosOptions.length > 0 ? (
                        <Form.Control as="select" value={paraId} onChange={(e) => setParaId(e.target.value)}>
                            {usuariosOptions.map((o) => (
                                <option key={o.id} value={o.id}>{o.nombre}</option>
                            ))}
                        </Form.Control>
                    ) : (
                        <Form.Control plaintext readOnly value={targetLabel} />
                    )}
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Actividad (Gantt)</Form.Label>
                    {cargando ? (
                        <div className="d-flex align-items-center gap-2 py-1">
                            <Spinner size="sm" animation="border" variant="primary" />
                            <span className="text-muted small">Cargando actividades...</span>
                        </div>
                    ) : (
                        <Form.Control
                            as="select"
                            value={actividadId}
                            onChange={(e) => setActividadId(e.target.value)}
                        >
                            <option value="">— Seleccione la actividad —</option>
                            {tareas.map((t) => (
                                <option key={t.id} value={t.id}>
                                    {t.name}
                                    {t.horas_estimadas && t.horas_estimadas > 0 ? ` (${t.horas_estimadas}h)` : ''}
                                </option>
                            ))}
                        </Form.Control>
                    )}
                    {taskNombre && (
                        <Form.Text className="text-muted">
                            Actividad seleccionada: <strong>{taskNombre}</strong>
                        </Form.Text>
                    )}
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Fecha</Form.Label>
                    <Form.Control
                        type="date"
                        value={fecha}
                        max={moment().format('YYYY-MM-DD')}
                        onChange={(e) => setFecha(e.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mb-0">
                    <Form.Label>Horas</Form.Label>
                    <Form.Control
                        type="number"
                        min="0.5"
                        max="24"
                        step="0.5"
                        value={horas}
                        onChange={(e) => setHoras(e.target.value)}
                        placeholder="0.5 – 24"
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={guardando}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleGuardar} disabled={guardando || cargando}>
                    {guardando ? (
                        <Spinner size="sm" animation="border" className="me-1" />
                    ) : (
                        <i className="bi bi-clock me-1" />
                    )}
                    Guardar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const mapStateToProps = (state) => ({
    usuarioActual: sessionSelectors.getUser(state),
});

export default connect(mapStateToProps)(RegistrarTiempoModal);